import { Component, DOCUMENT, DestroyRef, HostListener, computed, effect, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Header } from '../../ui/header/header';
import { Sidebar } from '../../ui/sidebar/sidebar';
import { ThemeSettings } from '../../ui/theme-settings/theme-settings';
import { Footer } from '../../ui/footer/footer';
import { AppLoader } from '../../ui/app-loader/app-loader';
import { LayoutService } from '../../../core/services/common/layout.service';
import { SessionTimeoutService, formatCountdown } from '../../../core/services/auth/session-timeout.service';
import { AppContextService } from '../../../core/services/context/app-context.service';
import { AuthService } from '../../../features/authentication/data-access/auth.service';

@Component({
  selector: 'app-content-layout',
  templateUrl: './content-layout.html',
  styleUrl: './content-layout.scss',
  imports: [NgClass, Header, Sidebar, RouterOutlet, Footer, AppLoader, ThemeSettings],
})
export class ContentLayout {
  readonly layoutService = inject(LayoutService);
  sessionTimeoutService = inject(SessionTimeoutService);
  /** The warning's countdown as m:ss. */
  readonly countdown = computed(() => formatCountdown(this.sessionTimeoutService.remainingSeconds()));
  /** Circumference of the countdown ring (r = 42), for its stroke-dasharray. */
  readonly ringLength = 2 * Math.PI * 42;
  /** How much of the ring is used up: 0 when the warning opens, the full length at sign-out. */
  readonly ringOffset = computed(() => {
    const total = this.sessionTimeoutService.warningSeconds;
    const left = Math.min(Math.max(this.sessionTimeoutService.remainingSeconds(), 0), total);
    return this.ringLength * (1 - left / total);
  });
  /** The last seconds before sign-out are shown in red. */
  readonly countdownUrgent = computed(() => this.sessionTimeoutService.remainingSeconds() <= 10);
  private readonly appContextService = inject(AppContextService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  /** The frame's classes: the side menu is folded away on a desktop, or slid over the page on a phone. */
  readonly containerClass = computed(() => {
    const state = this.layoutService.layoutState();
    return {
      'layout-static': true,
      'layout-static-inactive': state.staticMenuDesktopInactive,
      'layout-menu-toggling': state.menuToggling,
      'layout-mobile-active': state.mobileMenuActive,
    };
  });

  constructor() {
    // Marks the page as the signed-in frame: the application look applies to it, not to the sign-in pages.
    const html = this.document.documentElement;
    html.setAttribute('data-layout', 'shell');
    inject(DestroyRef).onDestroy(() => html.removeAttribute('data-layout'));

    this.sessionTimeoutService.start();

    if (this.authService.isLoggedIn) {
      this.appContextService.ensureMenuInitialized().pipe(takeUntilDestroyed()).subscribe();
    }

    // A menu slid over the page on a phone covers it: stop the page behind it from scrolling.
    effect(() => {
      document.body.classList.toggle('blocked-scroll', this.layoutService.layoutState().mobileMenuActive);
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.layoutService.closeMobileMenu();
        // The frame now stays put between pages, so a new page opens at its top (the page used to be
        // rebuilt together with the frame, which had that effect by accident).
        this.document.defaultView?.scrollTo(0, 0);
      });
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    // Escape closes the idle warning the way Extend session does. A session that is ending cannot be extended.
    if (this.sessionTimeoutService.showWarning() && !this.sessionTimeoutService.sessionEnding()) {
      this.sessionTimeoutService.extendSession();
    }
  }
}
